import type { Editor } from 'tinymce';

export interface LinkValues {
	url: string;
	text: string;
	title: string;
	/** HTML anchor target: '' for the current window, '_blank' for a new window. */
	target: string;
}

const DEFAULT_NEW_LINK_TARGET = '_blank';

// Scheme-less URLs get https:// (matching the editor's link_assume_external_targets); dangerous
// schemes (javascript:, data:, …) also fail this test and are prefixed into an inert value. We
// must sanitize here because applyLink's edit path writes via dom.setAttribs, bypassing TinyMCE.
const SAFE_URL = /^(https?:|mailto:|tel:|ftp:|#|\/|\{\{)/i;

const normalizeUrl = (url: string): string => (url && !SAFE_URL.test(url) ? `https://${url}` : url);

const encodeHtml = (value: string): string =>
	value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const getSelectedAnchor = (editor: Editor): HTMLAnchorElement | null =>
	editor.dom.getParent(editor.selection.getNode(), 'a[href]') as HTMLAnchorElement | null;

const getSelectedImage = (editor: Editor): HTMLImageElement | null => {
	const node = editor.selection.getNode();
	return node && node.nodeName === 'IMG' ? (node as HTMLImageElement) : null;
};

export const readLinkFromSelection = (editor: Editor): LinkValues => {
	const anchor = getSelectedAnchor(editor);
	if (anchor) {
		return {
			url: anchor.getAttribute('href') ?? '',
			text: anchor.textContent ?? '',
			title: anchor.getAttribute('title') ?? '',
			target: anchor.getAttribute('target') ?? '',
		};
	}

	return {
		url: '',
		text: editor.selection.getContent({ format: 'text' }) ?? '',
		title: '',
		target: DEFAULT_NEW_LINK_TARGET,
	};
};

export const applyLink = (editor: Editor, values: LinkValues): void => {
	const url = normalizeUrl(values.url.trim());
	const anchor = getSelectedAnchor(editor);

	if (!url) {
		if (anchor) {
			editor.execCommand('unlink');
		}
		return;
	}

	const text = values.text.trim() || url;
	const attribs: Record<string, string | null> = {
		href: url,
		title: values.title.trim() || null,
		target: values.target || null,
		rel: values.target === '_blank' ? 'noopener' : null,
	};

	if (anchor) {
		editor.dom.setAttribs(anchor, attribs);
		if ((anchor.textContent ?? '') !== text) {
			editor.dom.setHTML(anchor, encodeHtml(text));
		}
		return;
	}

	const attribString = Object.entries(attribs)
		.filter(([, value]) => value != null)
		.map(([key, value]) => `${key}="${encodeHtml(String(value))}"`)
		.join(' ');

	// An image selection has no text content, so getContent({format:'text'}) is empty and `text`
	// falls back to the URL. Wrap the image's own markup in the anchor rather than replacing it
	// with a plain text link.
	const image = getSelectedImage(editor);
	const inner = image ? editor.selection.getContent({ format: 'html' }) : encodeHtml(text);

	editor.insertContent(`<a ${attribString}>${inner}</a>`);
};
